/**
 * UNE RÉFÉRENCE N'EST PAS TOUJOURS UN NUMÉRO.
 *
 * Le champ « référence » a été rempli à la main pendant des années : parfois
 * c'est « 352119 », parfois c'est un paragraphe entier expliquant que le
 * client n'a rien fourni. Rendu en pastille à côté du nom, ce paragraphe
 * s'enroulait sur trois lignes et cassait la rangée — et toutes les rangées
 * voisines avec elle, puisqu'une table aligne ce qui a la même hauteur.
 *
 * Ici la référence tient TOUJOURS sur une ligne, sous le nom. Si elle ne
 * rentre pas, un « + » ouvre une bulle avec le texte entier. La rangée garde
 * sa hauteur quoi qu'on ait écrit dedans.
 */
import { useEffect, useRef, useState } from 'react';
import type { Lang } from '@/data/types';

/** Longer than this is prose, not a reference: it gets the bubble. */
const LONG = 26;

export function RefChip({ value, lang }: { value: string | null | undefined; lang: Lang }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const text = (value ?? '').trim();
  if (!text) return null;
  const long = text.length > LONG;

  return (
    <span className="actes-ref" ref={box}>
      <span className="actes-ref-t" title={long ? undefined : text}>{long ? `${text.slice(0, LONG)}…` : text}</span>
      {long && (
        <>
          <button
            type="button"
            className={`actes-ref-more ${open ? 'on' : ''}`}
            aria-expanded={open}
            aria-label={lang === 'fr' ? 'Voir la référence entière' : 'See the whole reference'}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          >
            +
          </button>
          {open && (
            <span className="actes-ref-pop" onClick={(e) => e.stopPropagation()}>
              <em>{lang === 'fr' ? 'Référence' : 'Reference'}</em>
              {text}
            </span>
          )}
        </>
      )}
    </span>
  );
}
